'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { PrismicNextImage } from '@prismicio/next';
import { Settings } from '@/app/lib/settings';
import { isFilled } from '@prismicio/helpers';
import { RichText } from '@/app/lib/RichText';
import { dashify, sliceStyle } from '@/app/lib/utils';
import { MoreLink } from '@/app/lib/MoreLink';
import { BackgroundImage } from "@/app/lib/ParallaxBackground";
import { usePathname } from 'next/navigation';
import { parseISO, isSameDay, format } from 'date-fns';
import { RevealTitle } from '@/app/lib/RevealTitle';
import { RevealOnScroll } from '@/app/lib/RevealOnScroll';
import { ConditionalLink } from '@/app/lib/ConditionalLink';
import Chevron from '@/app/assets/images/icon_chevron.svg';
import sharedStyles from '@/app/shared.module.scss';
import styles from '@/slices/Lineup/lineup.module.scss';

const FullLineup = ({ slice, pageData }) => {
	const pathname = usePathname();

	const defaultDateLabel = 'All Days';
	const [selectedDate, setSelectedDate] = useState(defaultDateLabel);

	// Extract unique dates from person array (checking date, date_2, date_3, and date_4 fields)
	const dateOptions = useMemo(() => {
		if (!pageData?.data?.person?.length) return [];

		const dateMap = new Map();

		// Helper function to process a date field
		const processDateField = (dateStr) => {
			if (!isFilled.date(dateStr)) return;

			const dateObj = parseISO(dateStr);
			if (isNaN(dateObj.getTime())) return;

			// Format for display: "Thur 23 Feb" - no timezone transformation
			// Use custom day abbreviations to show "Tues" and "Thur" instead of "Tue" and "Thu"
			const dayAbbr = format(dateObj, 'EEE'); // e.g., "Tue", "Thu"
			const dayLabel = dayAbbr === 'Tue' ? 'Tues' : dayAbbr === 'Thu' ? 'Thur' : dayAbbr;
			const dayNumber = format(dateObj, 'd'); // e.g., "23"
			const monthLabel = format(dateObj, 'MMM'); // e.g., "Feb"
			const displayDate = `${dayLabel} ${dayNumber} ${monthLabel}`;
			// Format for URL slug: "23-feb" - no timezone transformation
			const urlSlug = format(dateObj, 'd-MMM').toLowerCase();

			if (!dateMap.has(urlSlug)) {
				dateMap.set(urlSlug, {
					displayDate,
					dayLabel,
					dayNumber,
					monthLabel,
					dateStr,
					dateObj,
					urlSlug
				});
			}
		};

		pageData.data.person.forEach(person => {
			// Check all date fields: date, date_2, date_3, and date_4
			processDateField(person?.date);
			processDateField(person?.date_2);
			processDateField(person?.date_3);
			processDateField(person?.date_4);
		});

		// Sort by date
		return Array.from(dateMap.values()).sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
	}, [pageData?.data?.person]);

	// Get date slug from URL - also handles browser back/forward navigation
	useEffect(() => {
		const updateSelectedDate = () => {
			const currentPath = window.location.pathname;
			const pathSegments = currentPath.split('/').filter(Boolean);
			const lastSegment = pathSegments[pathSegments.length - 1];

			// Check if last segment is a date slug (not 'lineup' or an event UID)
			if (lastSegment && lastSegment !== 'lineup') {
				// Check if it's a date slug by trying to match it
				const matchingDate = dateOptions.find(opt => opt.urlSlug === lastSegment);
				if (matchingDate) {
					setSelectedDate(matchingDate.displayDate);
					return;
				}
			}

			// Default to "All Days"
			setSelectedDate(defaultDateLabel);
		};

		// Update on initial load and pathname change
		updateSelectedDate();

		// Listen for browser back/forward navigation
		window.addEventListener('popstate', updateSelectedDate);

		return () => {
			window.removeEventListener('popstate', updateSelectedDate);
		};
	}, [pathname, dateOptions]);

	// Filter people based on selected date (checking date, date_2, date_3, and date_4 fields)
	const filteredPeople = useMemo(() => {
		if (!pageData?.data?.person?.length) return [];

		if (selectedDate === defaultDateLabel) {
			return pageData.data.person;
		}

		// Find the selected date option
		const selectedOption = dateOptions.find(opt => opt.displayDate === selectedDate);
		if (!selectedOption) return pageData.data.person;

		// Filter people by matching date in any of the date fields
		return pageData.data.person.filter(person => {
			// Helper function to check if a date field matches the selected date
			const checkDateField = (dateStr) => {
				if (!isFilled.date(dateStr)) return false;
				const personDate = parseISO(dateStr);
				if (isNaN(personDate.getTime())) return false;
				return isSameDay(personDate, selectedOption.dateObj);
			};

			// Check all date fields: date, date_2, date_3, and date_4
			return checkDateField(person?.date) ||
				checkDateField(person?.date_2) ||
				checkDateField(person?.date_3) ||
				checkDateField(person?.date_4);
		});
	}, [pageData?.data?.person, selectedDate, dateOptions]);

	const handleDateSelect = useCallback((dateOption) => {
		if (dateOption === 'All Days') {
			setSelectedDate(defaultDateLabel);
			// Navigate to base lineup URL - remove date slug if present
			const pathSegments = pathname.split('/').filter(Boolean);
			const lastSegment = pathSegments[pathSegments.length - 1];

			// Check if last segment is a date slug
			const isDateSlug = dateOptions.some(opt => opt.urlSlug === lastSegment);

			if (isDateSlug) {
				// Remove the date slug
				pathSegments.pop();
				const basePath = '/' + pathSegments.join('/');
				// Silently update URL without page reload
				window.history.pushState({}, '', basePath || '/lineup');
			} else {
				// Already at base lineup URL, no change needed
				return;
			}
		} else {
			setSelectedDate(dateOption.displayDate);
			// Navigate to lineup with date slug
			const pathSegments = pathname.split('/').filter(Boolean);
			const lastSegment = pathSegments[pathSegments.length - 1];

			// Check if last segment is already a date slug
			const isDateSlug = dateOptions.some(opt => opt.urlSlug === lastSegment);

			if (isDateSlug) {
				// Replace the date slug
				pathSegments[pathSegments.length - 1] = dateOption.urlSlug;
			} else {
				// Add the date slug
				pathSegments.push(dateOption.urlSlug);
			}

			const newPath = '/' + pathSegments.join('/');
			// Silently update URL without page reload
			window.history.pushState({}, '', newPath);
		}
	}, [pathname, dateOptions]);

	return (
		<BackgroundImage slicePrimary={slice?.primary}>
			<div className={clsx(styles.lineup, styles.full_lineup)} style={sliceStyle(slice?.primary)}>

				{/* title */}
				<div className={clsx(sharedStyles.top)}>
					<div className={clsx(sharedStyles.left)}>
						{isFilled.keyText(slice?.primary?.title) && (
							<h1 className={clsx(sharedStyles.h1)} id={dashify(slice?.primary?.title)}>
								<RevealTitle size='small'>{slice?.primary?.title}</RevealTitle>
							</h1>
						)}
					</div>

					{pageData?.data?.show_date_filter && dateOptions.length > 0 && (
						<div className={clsx(sharedStyles.filters, styles.lineupFilters)}>
							<div className={clsx(sharedStyles.label_wrap)}>
								<div className={clsx(sharedStyles.label)}>Filter by day</div>
								<Chevron className={clsx(sharedStyles.chevron)} />
							</div>

							<div className={clsx(sharedStyles.filterButtonsContainer)}>
								<span
									onClick={() => handleDateSelect('All Days')}
									className={clsx(sharedStyles.item, sharedStyles.whiteButton, sharedStyles.pinkText, sharedStyles.fullWidthMobile)}
								>
									All Days
								</span>

								{dateOptions.map((dateOption) => {
									const isHighlighted = dateOption.displayDate === selectedDate;
									return (
										<span
											key={dateOption.urlSlug}
											onClick={() => handleDateSelect(dateOption)}
											className={clsx(sharedStyles.item, sharedStyles.whiteButton, {
												[sharedStyles.highlighted]: isHighlighted,
											})}
										>
											<span className={clsx(sharedStyles.dayLabel)}>{dateOption.dayLabel}</span> {dateOption.dayNumber} {dateOption.monthLabel}
										</span>
									);
								})}
							</div>
						</div>
					)}
				</div>

				{/* people */}
				{filteredPeople?.length ? (
					<div className={clsx(styles.people)}>
						{filteredPeople.map((person, index) => {
							return (
								<RevealOnScroll scrollDelayRem={(index % 4) * 5} key={index}>
									<ConditionalLink url={person?.url} condition={isFilled.link(person?.url)} className={clsx(styles.person)}>
										{isFilled.image(person?.photograph) && (
											<div className={clsx(styles.photograph)}>
												<PrismicNextImage field={person?.photograph} alt={person?.name} fallbackAlt='' />
											</div>
										)}
										<div className={clsx(styles.info)}>
											{isFilled.keyText(person?.full_name) && (<h2 className={clsx(styles.name)} style={{ color: slice?.primary?.talent_color || `` }}>{person?.full_name}</h2>)}
											{isFilled.richText(person?.description) && (<RichText field={person?.description} />)}
										</div>
									</ConditionalLink>
								</RevealOnScroll>
							)
						})}
					</div>
				) : null}

				{/* back link */}
				<MoreLink
					link={`/${Settings.eventsPrefix}/${pageData?.uid}`}
					text={"Back to event"}
					slice={slice}
					back={true}
				/>

			</div>
		</BackgroundImage>
	);
}

export default FullLineup;
